import React from "react";
import { DndProvider } from "react-dnd";
import createHTML5Backend from "react-dnd-html5-backend";

import { IClass, IFile, IStudent } from "../../../state";
import { MAX_IMAGE_SIZE_MB } from "../../../constants/upload";
import { Breadcrumb } from "../../../molecules/Breadcrumb";
import guideViewImage from "../../../assets/images/upload_photo.png";
import speechBubbleSvg from "../../../assets/svg/speech_bubble.svg";

import StudentListItem, { StudentWithUploadedFile } from "./StudentListItem";
import UploadedPhotoListItem from "./UploadedPhotoListItem";
import styles from "./UploadStudentRealPhotos.module.scss";

type Props = {
  calling: boolean;
  students: IStudent[];
  classes: IClass[];
  next_classes?: IClass[];
  selectedClassId: number;
  uploadedPhotos?: IFile[];
  onChangeClassId: (classId: number) => void;
  onUpload: (files: File[]) => void;
  onDrop: (params: {
    photo_id: number;
    student: StudentWithUploadedFile;
  }) => void;
  onCancelDrop: (student: StudentWithUploadedFile) => void;
  onDeletePhoto: (student: StudentWithUploadedFile) => void;
  onSubmit: () => void;
  deleteStudentRealPhotoUploadedFile: (id: number) => void;
};

export default function UploadStudentRealPhotos({
  calling,
  students,
  classes,
  next_classes,
  uploadedPhotos,
  selectedClassId,
  onChangeClassId,
  onUpload,
  onDrop,
  onCancelDrop,
  onDeletePhoto,
  onSubmit,
  deleteStudentRealPhotoUploadedFile,
}: Props) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = e.currentTarget?.files;
    if (!uploadedFiles || !uploadedFiles.length) return;
    const oversizedFiles = Array.from(uploadedFiles).filter(
      file => file.size / 1024 / 1024 > MAX_IMAGE_SIZE_MB,
    );
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(", ");
      alert(
        `以下のファイルがサイズ上限（${MAX_IMAGE_SIZE_MB}MB）を超えています。\n${fileNames}`,
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    onUpload(Array.from(uploadedFiles));
  };

  return (
    <div className="px-4 pb-4">
      <Breadcrumb currentTitle="生徒顔写真登録" />
      <div className={styles.headerLayout}>
        <div className={styles.leftSection}>
          <div className={styles.titleRow}>
            <h3 className={styles.pageTitle}>生徒顔写真登録</h3>
            <div className={styles.pageDescription}>
              LOUNGE上で表示される、生徒の顔写真を登録できます。
              {"\n"}
              顔写真の設定が終わったら最後にページ左下の「確定」ボタンを押してください。
            </div>
          </div>

          <div className={styles.speechBubbleContainer}>
            <div className={styles.speechBubble}>
              <img
                src={speechBubbleSvg}
                alt=""
                className={styles.speechBubbleImg}
              />
              <p className={styles.speechBubbleText}>
                顔写真を登録することで、生徒一人ひとりの変化が把握しやすくなり、日々の声かけや
                <br />
                関わりの具体性が高まります。LOUNGEを効果的に活用するため、顔写真の登録を推奨しています。
              </p>
            </div>
          </div>
        </div>
        <div className={styles.rightSection}>
          <div className={styles.guideWrapper}>
            <p className={styles.guideLabel}>＼ このように表示されます ／</p>
            <img
              src={guideViewImage}
              alt="Guide View"
              className={styles.guideImage}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-3">
          <select
            name="class"
            className="form-select"
            aria-label="select"
            value={selectedClassId}
            disabled={calling}
            onChange={e => onChangeClassId(Number(e.target.value))}
          >
            <option value={0}>クラスを選択</option>
            {[...classes, ...(next_classes || [])].map(c => {
              return (
                <option key={c.id} value={c.id}>
                  {c.full_name} ({c.year})
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <DndProvider backend={createHTML5Backend}>
        <div className="row pt-4">
          <div className="col-lg-5 col-md-6">
            <div className={styles.studentsAreaWrapper}>
              <div className={styles.studentsAreaTitle}>
                <h4>生徒名</h4>
              </div>
              <div className={styles.studentsArea}>
                {students.length > 0 ? (
                  students.map(student => (
                    <StudentListItem
                      key={student.id}
                      student={student}
                      onDrop={onDrop}
                      onCancelDrop={onCancelDrop}
                      onDeletePhoto={onDeletePhoto}
                    />
                  ))
                ) : (
                  <div className="p-4">クラスを選択してください</div>
                )}
              </div>
            </div>
            <div className="row pt-4 justify-content-end">
              <div className="col-3">
                <button
                  className="btn btn-primary btn-lg"
                  disabled={!selectedClassId || calling}
                  onClick={onSubmit}
                >
                  確定
                </button>
              </div>
            </div>
          </div>
          <div className={`col-lg-7 col-md-6 ${styles.photoListWrapper}`}>
            <div className="row py-4">
              <div className="col-2">
                <h5>画像一覧</h5>
              </div>
              <div className="col-7">
                生徒画像を追加後、生徒ごとにドラッグ&ドロップで登録できます
              </div>
              <div className="col-3">
                <button
                  className="btn btn-light"
                  disabled={!selectedClassId || calling}
                  onClick={() => fileInputRef.current?.click()}
                >
                  画像を追加
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg, image/png, image/jpg"
                    multiple
                    disabled={!selectedClassId || calling}
                    className="d-none"
                    onChange={handleChangeFile}
                  />
                </button>
              </div>
            </div>
            <div className={`position-sticky ${styles.photoListSticky}`}>
              <div className={styles.photoListScrollArea}>
                {uploadedPhotos &&
                  uploadedPhotos.map(photo => (
                    <UploadedPhotoListItem
                      key={photo.id}
                      photo={photo}
                      deleteStudentRealPhotoUploadedFile={
                        deleteStudentRealPhotoUploadedFile
                      }
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </DndProvider>
    </div>
  );
}
